import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play, Check, Camera, CheckCircle2, Share2, Wallet, MoreHorizontal,
  Heart, MessageCircle, Send, Instagram, UtensilsCrossed, Store, Trophy, Building2, Ticket } from "lucide-react";
import { SpotLightbox } from "@/components/RobbieSpot";
import { PassCard } from "@/portal/loyalty/PassCard";
import { BELEGE_KURZ } from "@/components/Vertrauen";
import { PAKETE, CTA_NOTE } from "@/data/pakete";

/* Punkt N – ruhiges Startseiten-Redesign (Thomas, 6 Abschnitte).
   Regeln: Gelb nur für Haupt-Button + ein Wort in der Hero-Headline; Karten mit 1px-Linie (#E5E5E5),
   keine dicken Rahmen/harten Schatten; Überschriften in Satz-Schreibweise; viel Luft; nur EIN Video. */

const H = { fontFamily: "'Outfit', 'Poppins', sans-serif", fontWeight: 800 };
const B = { fontFamily: "'Poppins', sans-serif" };
const WARM = "#F7F4EE";   // sehr helles Warmgrau – Trenner
const GELB = "#FDD105";
const LINE = "#E5E5E5";

const up = (d = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] } });

const usePrefersReducedMotion = () => {
  const [reduce, setReduce] = useState(() => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(m.matches);
    on(); m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return reduce;
};

/* ---------- gemeinsame, ruhige Bausteine ---------- */
const BtnPrimary = ({ to, children, testId, className = "" }) => (
  <Link to={to} data-testid={testId} style={{ ...B, fontWeight: 700 }}
    className={`inline-flex w-fit items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#FDD105] px-7 py-3.5 text-[15px] font-bold text-[#111] shadow-[0_2px_10px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_22px_rgba(0,0,0,0.12)] ${className}`}>
    {children} <ArrowRight size={18} strokeWidth={2.5} aria-hidden="true" />
  </Link>
);

const TextLink = ({ to, onClick, children, testId }) => {
  const cls = "group inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#111] underline decoration-[#111]/25 decoration-1 underline-offset-4 transition-colors hover:decoration-[#111]";
  return to
    ? <Link to={to} data-testid={testId} style={{ ...B, fontWeight: 600 }} className={cls}>{children} <ArrowRight size={15} strokeWidth={2.4} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></Link>
    : <button type="button" onClick={onClick} data-testid={testId} style={{ ...B, fontWeight: 600 }} className={cls}>{children}</button>;
};

const SectionHead = ({ children, lead, center = false }) => (
  <div className={`${center ? "mx-auto text-center" : ""} max-w-[42ch]`}>
    <h2 style={H} className="text-[#111] leading-[1.12] text-[1.7rem] md:text-4xl">{children}</h2>
    {lead && <p style={B} className={`mt-4 text-[15px] md:text-base leading-relaxed text-[#5C574F] ${center ? "mx-auto" : ""} max-w-[48ch]`}>{lead}</p>}
  </div>
);

/* ---------- 1 · HERO ---------- */
const HERO_LOOP_MP4 = "/media/robbie/hero-loop.mp4";
const HERO_LOOP_WEBM = "/media/robbie/hero-loop.webm";
const HERO_LOOP_POSTER = "/media/robbie/hero-loop-poster.webp";

const HeroPhone = () => {
  const reduce = usePrefersReducedMotion();
  const [mountVideo, setMountVideo] = useState(false);
  useEffect(() => {
    if (reduce) return undefined;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = idle(() => setMountVideo(true));
    return () => cancel(id);
  }, [reduce]);
  return (
    <div className="relative flex items-center justify-center" data-testid="heron-phone">
      <div className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[76%] w-[104%] -translate-x-1/2 -translate-y-1/2 rounded-[46%] bg-[#FDD105]/25 blur-3xl" aria-hidden="true" />
      <div className="relative h-[520px] w-[290px] overflow-hidden rounded-[2.6rem] border border-[#111]/10 bg-[#111] shadow-[0_30px_60px_-30px_rgba(0,0,0,0.45)] md:h-[560px] md:w-[312px]">
        <span style={{ ...B, fontWeight: 700 }} className="absolute right-3 top-5 z-30 inline-flex items-center gap-1 rounded-full bg-[#34A853] px-2.5 py-1 text-[10.5px] font-bold text-white shadow-[0_2px_8px_rgba(0,0,0,0.25)]" data-testid="heron-phone-badge">
          <Check size={11} strokeWidth={3} aria-hidden="true" /> Freigegeben
        </span>
        <img src={HERO_LOOP_POSTER} alt="Robbie macht aus einem Foto Social Media, Google und Bildschirm" width="312" height="560" fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
        {!reduce && mountVideo && (
          <video poster={HERO_LOOP_POSTER} muted autoPlay loop playsInline preload="auto" className="absolute inset-0 h-full w-full object-cover" data-testid="heron-phone-video" aria-hidden="true">
            <source src={HERO_LOOP_WEBM} type="video/webm" />
            <source src={HERO_LOOP_MP4} type="video/mp4" />
          </video>
        )}
      </div>
    </div>
  );
};

export const HeroN = () => {
  const [video, setVideo] = useState(false);
  const filmBtn = useRef(null);
  return (
    <section className="relative overflow-hidden" style={{ background: WARM }} data-testid="heron">
      <SpotLightbox open={video} onOpenChange={setVideo} returnFocusRef={filmBtn} />
      <div className="mx-auto grid max-w-[1160px] items-center gap-x-16 gap-y-12 px-6 pt-32 pb-20 md:px-10 md:pt-36 md:pb-28 lg:grid-cols-[1.04fr_0.96fr]">
        <div className="flex flex-col gap-7">
          <motion.h1 {...up(0)} style={H} className="text-[#111] leading-[1.06] tracking-[-0.015em] text-[2.4rem] sm:text-5xl lg:text-[clamp(2.6rem,3.6vw,3.6rem)]" data-testid="heron-headline">
            Du kümmerst dich um deine Kunden.{" "}
            <span className="box-decoration-clone rounded-[4px] bg-[#FDD105] px-2 py-0.5">Robbie</span> um den Rest.
          </motion.h1>
          <motion.p {...up(0.08)} style={B} className="max-w-[44ch] text-[16px] md:text-lg leading-relaxed text-[#4A4A4A]" data-testid="heron-lead">
            Ein Foto per WhatsApp – Robbie macht Posts, Google-Beiträge und mehr daraus. Du tippst nur „Freigeben“.
          </motion.p>
          <motion.div {...up(0.14)} className="flex flex-wrap items-center gap-x-7 gap-y-4">
            <BtnPrimary to="/kunden/registrieren" testId="heron-cta-test">30 Tage kostenlos testen</BtnPrimary>
            <button type="button" ref={filmBtn} onClick={() => setVideo(true)} data-testid="heron-cta-film" style={{ ...B, fontWeight: 600 }}
              className="group inline-flex items-center gap-2 text-[15px] font-semibold text-[#111]">
              <span className="grid h-9 w-9 place-items-center rounded-full border border-[#111]/15 bg-white text-[#111] transition-colors group-hover:border-[#111]"><Play size={14} strokeWidth={2.5} fill="currentColor" className="ml-0.5" /></span>
              <span className="underline decoration-[#111]/25 decoration-1 underline-offset-4 transition-colors group-hover:decoration-[#111]">Film ansehen (33 s)</span>
            </button>
          </motion.div>
          <motion.p {...up(0.2)} style={B} className="text-[13.5px] text-[#8A857C]" data-testid="heron-note">
            Ohne Zahlungsdaten · endet automatisch
          </motion.p>
        </div>
        <motion.div {...up(0.12)}><HeroPhone /></motion.div>
      </div>
    </section>
  );
};

/* ---------- 2 · SO FUNKTIONIERT'S ---------- */
const SCHRITTE = [
  { icon: Camera, titel: "Foto schicken", text: "Per WhatsApp an Robbie – wie an einen Kollegen." },
  { icon: CheckCircle2, titel: "Freigeben", text: "Robbie zeigt dir den fertigen Entwurf. Ein Tipp genügt." },
  { icon: Share2, titel: "Überall sichtbar", text: "Instagram, Google und dein Bildschirm – gleichzeitig." },
];

export const SoFunktioniertN = () => (
  <section id="sofunktioniert" className="scroll-mt-24 bg-white" data-testid="heron-sofunktioniert">
    <div className="mx-auto max-w-[1160px] px-6 py-20 md:px-10 md:py-32">
      <SectionHead>So funktioniert's.</SectionHead>
      <div className="mt-12 grid gap-6 md:mt-16 md:grid-cols-3 md:gap-10">
        {SCHRITTE.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div key={s.titel} {...up(i * 0.08)} className="flex flex-col items-start" data-testid={`heron-schritt-${i + 1}`}>
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#F7F4EE] text-[#111]" aria-hidden="true"><Icon size={24} strokeWidth={2} /></span>
              <h3 style={H} className="mt-5 text-xl text-[#111]">{s.titel}</h3>
              <p style={B} className="mt-2 text-[15px] leading-relaxed text-[#5C574F]">{s.text}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- 3 · DAS MACHT ROBBIE FÜR DICH (3 Beispielkarten) ---------- */
const DEMO_SHOP = { name: "Robbies Kaffeeküche", brand: { style: "espresso", color: "#8a5a2b", logo_url: "/demo/cafe_logo.webp", cover_url: "/demo/cafe_interior.webp" }, pass: { goal: 10, icon: "coffee", reward: "1 Kaffee gratis" } };
const GOOGLE_BADGE = "/wallet/google-wallet-de.svg";
const APPLE_BADGE = "/wallet/apple-wallet-de.svg";

export const DasMachtRobbieN = () => {
  const [just, setJust] = useState(false);
  useEffect(() => {
    const id = setInterval(() => { setJust(true); setTimeout(() => setJust(false), 600); }, 3600);
    return () => clearInterval(id);
  }, []);
  const hideBadge = (e) => { e.currentTarget.style.display = "none"; };
  return (
    <section id="beispiele" className="scroll-mt-24" style={{ background: WARM }} data-testid="heron-dasmacht">
      <div className="mx-auto max-w-[1160px] px-6 py-20 md:px-10 md:py-32">
        <SectionHead lead="Beispiele vom Demo-Betrieb – keine echten Kundendaten.">Das macht Robbie für dich.</SectionHead>
        <div className="mt-12 grid items-start gap-6 md:mt-16 md:grid-cols-3 md:gap-8">

          {/* Karte 1 – Kundenkarte mit Wallet-Badges */}
          <motion.article {...up(0)} className="flex flex-col rounded-3xl border border-[#E5E5E5] bg-white p-6" data-testid="heron-card-kundenkarte">
            <h3 style={H} className="text-lg text-[#111]">Digitale Kundenkarte</h3>
            <div className="mx-auto mt-5 w-full max-w-[230px]">
              <PassCard shop={DEMO_SHOP} stamps={3} justStamped={just} testId="heron-demo-passcard" />
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5" data-testid="heron-wallet-badges" aria-hidden="true">
              <img src={GOOGLE_BADGE} alt="" className="block h-[38px] w-auto" />
              <img src={APPLE_BADGE} alt="" className="block h-[38px] w-auto" onError={hideBadge} />
            </div>
          </motion.article>

          {/* Karte 2 – Google-Beitrag */}
          <motion.article {...up(0.08)} className="flex flex-col overflow-hidden rounded-3xl border border-[#E5E5E5] bg-white" data-testid="heron-card-google">
            <img src="/demo/latte_art.webp" alt="Flat White – Beispiel-Beitrag" className="h-36 w-full object-cover" />
            <div className="flex flex-1 flex-col p-6">
              <h3 style={H} className="text-lg text-[#111]">Google-Beitrag</h3>
              <div className="mt-3 flex items-center gap-2">
                <span className="grid h-7 w-7 place-items-center rounded-full border border-black/10 bg-white text-[14px] font-black" style={{ color: "#4285F4" }} aria-hidden="true">G</span>
                <div className="leading-tight"><p style={{ ...B, fontWeight: 700 }} className="text-[13px] font-bold text-[#111]">Robbies Kaffeeküche</p><p className="text-[11px] text-black/50">Unternehmensprofil</p></div>
              </div>
              <p style={B} className="mt-3 text-[13px] leading-relaxed text-[#444]">Frisch gebrüht: unsere neue Hausröstung als Flat White – heute bei uns bis 18 Uhr.</p>
            </div>
          </motion.article>

          {/* Karte 3 – Instagram */}
          <motion.article {...up(0.16)} className="flex flex-col overflow-hidden rounded-3xl border border-[#E5E5E5] bg-white" data-testid="heron-card-instagram">
            <div className="flex items-center gap-2 p-4 pb-3">
              <img src="/demo/cafe_logo.webp" alt="" className="h-7 w-7 rounded-full border border-black/10 object-cover" />
              <p style={{ ...B, fontWeight: 700 }} className="flex-1 text-[13px] font-bold text-[#111]">robbies.kaffeekueche</p>
              <MoreHorizontal size={18} className="text-black/40" aria-hidden="true" />
            </div>
            <img src="/demo/latte_art.webp" alt="Latte Art – Beispiel-Post" className="h-44 w-full object-cover" />
            <div className="flex flex-1 flex-col p-5">
              <div className="flex items-center gap-4">
                <Heart size={20} strokeWidth={2} className="text-[#111]" aria-hidden="true" />
                <MessageCircle size={20} strokeWidth={2} className="text-[#111]" aria-hidden="true" />
                <Send size={20} strokeWidth={2} className="text-[#111]" aria-hidden="true" />
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-black/50"><Instagram size={13} strokeWidth={2.2} aria-hidden="true" /> Instagram</span>
              </div>
              <p className="mt-3 text-[12.5px] text-black/60"><span style={{ ...B, fontWeight: 700 }} className="font-bold text-[#111]">robbies.kaffeekueche</span> Hausröstung, frisch im Glas.</p>
            </div>
          </motion.article>

        </div>
        <p style={B} className="mt-6 text-[12px] text-[#8A857C]" data-testid="heron-dasmacht-hinweis">Beispiele – keine echten Kundendaten.</p>
      </div>
    </section>
  );
};

/* ---------- 4 · FÜR DEINE BRANCHE (Tabs, ruhiges Bild, kein Video) ---------- */
const BRANCHEN_N = [
  { id: "gastronomie", titel: "Gastronomie", icon: UtensilsCrossed, img: "/video/szene-gastronomie.webp", alt: "Robbie fotografiert ein Tagesgericht an der Theke", to: "/fallbeispiele/gastronomie", link: "Für die Gastronomie",
    punkte: ["Tagesgericht fotografieren – Beitrag, Google-Meldung und Gastraum-Bildschirm.", "Antworten auf Google-Bewertungen, Aktion für ruhige Tage.", "Kundenkarte, Newsletter und WhatsApp für Stammgäste."] },
  { id: "einzelhandel", titel: "Einzelhandel", icon: Store, img: "/video/szene-einzelhandel.webp", alt: "Ladeninhaberin sieht das frisch fotografierte Produktfoto auf dem Handy", to: "/fallbeispiele/einzelhandel", link: "Für den Einzelhandel",
    punkte: ["Neue Ware einmal fotografieren – Schaufenster-Bildschirm und Instagram laufen mit.", "Angebote posten, Google-Profil aktuell, Reservierung per Link.", "Sammelpass in der Wallet, Newsletter zu neuer Ware."] },
  { id: "sportverein", titel: "Sportverein", icon: Trophy, img: "/video/szene-sportverein.webp", alt: "Robbie scannt am Stadiontor das Ticket eines Fans", to: "/fallbeispiele/sportvereine", link: "Für Vereine",
    punkte: ["Spielplan und Spielbericht auf Website, Instagram und Google.", "Tickets und Saisonkarten online, Einlass per QR-Code.", "Mitglieder per Newsletter und WhatsApp auf dem Laufenden."] },
  { id: "unternehmen", titel: "Unternehmen", icon: Building2, img: "/video/szene-social.webp", alt: "Robbie hält im Büro ein Handy mit dem fertigen Social-Media-Beitrag hoch", to: "/dein-buero", link: "Für Unternehmen",
    punkte: ["Posts, Website und Google-Profil aus einem Guss.", "Anfragen landen sortiert im Postfach.", "Newsletter an Kunden, Belege direkt an Lexware."] },
  { id: "events", titel: "Vereine & Events", icon: Ticket, img: "/video/szene-sportverein.webp", alt: "Einlass per QR-Scan bei einer Veranstaltung", to: "/tickets", link: "Tickets ansehen",
    punkte: ["Spieltag, Konzert oder Vereinsfest – angekündigt auf Social, Google und Bildschirm.", "Tickets online verkaufen: 0 € Grundgebühr, ab 0,29 € netto je Ticket.", "Einlass per QR-Scan mit dem Handy, Dauerkarten und Wallet-Tickets inklusive."] },
];

export const BrancheN = () => {
  const [tab, setTab] = useState(0);
  const aktiv = BRANCHEN_N[tab];
  const AktivIcon = aktiv.icon;
  return (
    <section id="branche" className="scroll-mt-24 bg-white" data-testid="heron-branche">
      <div className="mx-auto max-w-[1160px] px-6 py-20 md:px-10 md:py-32">
        <SectionHead>Für deine Branche.</SectionHead>
        <div role="tablist" aria-label="Branche wählen" className="mt-8 flex flex-wrap gap-2.5" data-testid="heron-branche-tabs">
          {BRANCHEN_N.map((t, i) => {
            const TabIcon = t.icon;
            const on = i === tab;
            return (
              <button key={t.id} role="tab" aria-selected={on} type="button" onClick={() => setTab(i)} style={{ ...B, fontWeight: 600 }}
                className={`inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111] focus-visible:ring-offset-2 ${on ? "bg-[#111] text-white" : "border border-[#E5E5E5] bg-white text-[#111] hover:border-[#111]"}`}
                data-testid={`heron-branche-tab-${t.id}`}>
                <TabIcon size={16} strokeWidth={2.2} aria-hidden="true" /> {t.titel}
              </button>
            );
          })}
        </div>
        <motion.div key={aktiv.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="mt-10 grid items-center gap-8 md:grid-cols-2 md:gap-14" data-testid={`heron-branche-panel-${aktiv.id}`}>
          <div className="overflow-hidden rounded-[28px] border border-[#E5E5E5] bg-[#ECECE8] aspect-[4/3]">
            <img src={aktiv.img} alt={aktiv.alt} width="800" height="600" loading="lazy" decoding="async" className="h-full w-full object-cover" data-testid={`heron-branche-bild-${aktiv.id}`} />
          </div>
          <div>
            <span className="inline-flex items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#F7F4EE] text-[#111]" aria-hidden="true"><AktivIcon size={22} strokeWidth={2} /></span>
              <h3 style={H} className="text-2xl text-[#111]">{aktiv.titel}</h3>
            </span>
            <ul className="mt-6 space-y-3.5" data-testid={`heron-branche-punkte-${aktiv.id}`}>
              {aktiv.punkte.map((p) => (
                <li key={p} className="flex gap-3" style={B}>
                  <Check size={18} strokeWidth={2.5} className="mt-0.5 shrink-0 text-[#111]" aria-hidden="true" />
                  <span className="text-[15px] leading-relaxed text-[#4A4A4A]">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7"><TextLink to={aktiv.to} testId={`heron-branche-link-${aktiv.id}`}>{aktiv.link}</TextLink></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------- 5 · KUNDEN AUS HERFORD (2 Zahlen + 3 Zitate) ---------- */
const STIMMEN_N = [
  { zitat: "Fast zwölf Jahre vertrauensvolle Zusammenarbeit – immer ehrlich, immer individuell.", wer: "Schuhhaus Tscheche", ort: "Herford", logo: "/img/logos/tscheche.png" },
  { zitat: "Seit DIMEDIAL unseren Instagram-Auftritt übernommen hat, haben wir in 90 Tagen die 250.000 Aufrufe geknackt.", wer: "BBG Herford", ort: "Basketball Herford", logo: "/img/logos/bbg-herford.png" },
  { zitat: "Wir arbeiten seit über elf Jahren vertrauensvoll zusammen – ganz großes Kino.", wer: "Capitol 3D Kino", ort: "Herford", logo: "/img/logos/capitol-kino.png" },
];

export const KundenHerfordN = () => (
  <section id="kunden" className="scroll-mt-24" style={{ background: WARM }} data-testid="heron-kunden">
    <div className="mx-auto max-w-[1160px] px-6 py-20 md:px-10 md:py-32">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <SectionHead lead="Zwei vom Kunden freigegebene Belege und drei Stimmen von Betrieben, die uns seit Jahren begleiten.">Kunden aus Herford.</SectionHead>
        <TextLink to="/referenzen" testId="heron-kunden-mehr">Alle Referenzen ansehen</TextLink>
      </div>

      {/* 2 Zahlen */}
      <div className="mt-10 grid gap-6 md:grid-cols-2" data-testid="heron-kunden-zahlen">
        {BELEGE_KURZ.map((k) => (
          <div key={k.testId} className="grid grid-cols-[80px_1fr] items-center gap-4 rounded-3xl border border-[#E5E5E5] bg-white p-5 md:grid-cols-[104px_1fr]" data-testid={k.testId}>
            <img src={k.img} alt={k.alt} width="400" height="500" loading="lazy" className="w-full rounded-2xl border border-[#E5E5E5] object-cover aspect-[4/5]" />
            <div className="min-w-0">
              <p style={{ ...B, fontWeight: 700 }} className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8A857C]">{k.wer}</p>
              <p style={H} className="mt-1 text-4xl text-[#111] break-words" data-testid={`${k.testId}-wert`}>{k.wert}</p>
              <p style={B} className="mt-1 text-[14px] leading-snug text-[#4A4A4A]">{k.was}</p>
              <p style={B} className="mt-2 text-[12px] text-[#8A857C]">{k.quelle}</p>
            </div>
          </div>
        ))}
      </div>
      <p style={B} className="mt-3 text-[12px] text-[#8A857C]" data-testid="heron-kunden-hinweis">Aufrufe zeigen Sichtbarkeit – keine garantierten Verkäufe. Beide Auftritte laufen vollständig über DIMEDIAL.</p>

      {/* 3 Zitate in einer Reihe */}
      <ul className="mt-8 grid gap-6 md:grid-cols-3" data-testid="heron-kunden-stimmen">
        {STIMMEN_N.map((s, i) => (
          <li key={s.wer} className="flex h-full flex-col rounded-3xl border border-[#E5E5E5] bg-white p-6" data-testid={`heron-stimme-${i + 1}`}>
            <div className="flex h-9 items-center"><img src={s.logo} alt={`Logo ${s.wer}`} width="447" height="447" loading="lazy" className="max-h-8 w-auto max-w-[130px] object-contain object-left" /></div>
            <blockquote style={{ ...B }} className="mt-4 flex-1 text-[15px] leading-relaxed text-[#111]">„{s.zitat}“</blockquote>
            <footer className="mt-4 border-t border-[#E5E5E5] pt-3">
              <p style={{ ...B, fontWeight: 700 }} className="text-[13px] font-bold text-[#111]">{s.wer}</p>
              <p style={B} className="mt-0.5 text-[12px] text-[#8A857C]">{s.ort}</p>
            </footer>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ---------- 6a · PREISE (ruhige Karten, Inhalte/Preise unverändert) ---------- */
const PREIS_PUNKTE = {
  start: ["10 Beiträge im Monat", "Onepage-Website", "2 Bildschirme im Laden"],
  plus: ["25 Beiträge im Monat", "Website mit bis zu 5 Seiten", "5 Bildschirme im Laden"],
  team: ["40 Beiträge im Monat", "Website mit bis zu 10 Seiten", "Persönliche Beratung jeden Monat"],
};

export const PreiseN = () => (
  <section id="preise" className="scroll-mt-24 bg-white" data-testid="heron-preise">
    <div className="mx-auto max-w-[1160px] px-6 py-20 md:px-10 md:py-32">
      <SectionHead lead="Alle Bausteine sind in jedem Paket enthalten – die Pakete unterscheiden sich nur in der Menge. Netto zzgl. MwSt., monatlich kündbar.">Ab 59 € im Monat – weniger als 2 € am Tag.</SectionHead>
      <div className="mt-12 grid gap-6 md:mt-16 sm:grid-cols-3">
        {PAKETE.map((p) => {
          const empfehlung = p.code === "plus";
          const name = p.name.charAt(0) + p.name.slice(1).toLowerCase();
          return (
            <motion.div key={p.code} {...up(0.05)} className={`relative flex flex-col rounded-3xl bg-white p-7 ${empfehlung ? "border border-[#111] shadow-[0_10px_30px_-18px_rgba(0,0,0,0.35)]" : "border border-[#E5E5E5]"}`} data-testid={`heron-preise-${p.code}`}>
              {empfehlung && <span style={{ ...B, fontWeight: 700 }} className="absolute -top-3 left-7 rounded-full bg-[#111] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white" data-testid="heron-preise-plus-empfehlung">Empfehlung</span>}
              <p style={H} className="text-xl text-[#111]">{name}</p>
              <p style={H} className="mt-3 text-4xl leading-none text-[#111]" data-testid={`heron-preis-${p.code}`}>{p.preis} €<span style={B} className="text-sm font-medium text-[#8A857C]"> / Monat</span></p>
              <p style={B} className="mt-3 text-[14px] leading-relaxed text-[#5C574F]">{p.fuer}</p>
              <ul className="mt-5 space-y-2.5" data-testid={`heron-preise-${p.code}-punkte`}>
                {PREIS_PUNKTE[p.code].map((pt) => (
                  <li key={pt} className="flex gap-2.5" style={B}><Check size={17} strokeWidth={2.5} className="mt-0.5 shrink-0 text-[#111]" aria-hidden="true" /><span className="text-[14px] leading-relaxed text-[#111]">{pt}</span></li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
        <BtnPrimary to="/kunden/registrieren" testId="heron-preise-cta">30 Tage kostenlos testen</BtnPrimary>
        <TextLink to="/preise" testId="heron-preise-details">Pakete vergleichen</TextLink>
      </div>
      <p style={B} className="mt-4 text-[12px] leading-relaxed text-[#8A857C]" data-testid="heron-preise-klein">Ticketing ohne Paket nutzbar – 0 € Grundgebühr, Gebühr nur je verkauftem Ticket. Studio-Aufträge nach Festpreis. {CTA_NOTE}</p>
    </div>
  </section>
);

/* ---------- 6c · ABSCHLUSS (schlicht, heller Hintergrund, gelber Button) ---------- */
export const AbschlussN = () => (
  <section id="abschluss" className="scroll-mt-24" style={{ background: WARM }} data-testid="heron-abschluss">
    <div className="mx-auto max-w-[1160px] px-6 py-20 md:px-10 md:py-28">
      <div className="rounded-[32px] border border-[#E5E5E5] bg-white p-8 md:p-14">
        <div className="mx-auto max-w-[60ch] text-center">
          <h2 style={H} className="text-[#111] leading-[1.1] text-[1.8rem] md:text-4xl" data-testid="heron-abschluss-headline">Werde sichtbar – und bleib es.</h2>
          <p style={B} className="mx-auto mt-5 max-w-[52ch] text-[15px] md:text-base leading-relaxed text-[#5C574F]" data-testid="heron-abschluss-text">
            Nach dem Klick: E-Mail eintragen, Bestätigungslink öffnen, Bereich wählen – und du bist in der Robbie-App. 30 Tage alle Bausteine nutzen, ohne Zahlungsdaten.
          </p>
          <div className="mt-8 flex flex-col items-center gap-4">
            <BtnPrimary to="/kunden/registrieren" testId="heron-abschluss-cta">30 Tage kostenlos testen</BtnPrimary>
            <p style={B} className="text-[13px] text-[#8A857C]" data-testid="heron-abschluss-note">{CTA_NOTE}</p>
            <p style={B} className="text-[14px] text-[#5C574F]">Lieber erst sprechen? <Link to="/kontakt" className="font-semibold text-[#111] underline underline-offset-4 decoration-[#111]/25 hover:decoration-[#111]" data-testid="heron-abschluss-kontakt">Kontakt aufnehmen</Link> – persönlich aus Herford.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
